import React, { useContext } from 'react';

import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItemGroup } from '@atlaskit/dropdown-menu-12';
import MoreIcon from '@atlaskit/icon/glyph/more';

import { DiffContext } from 'src/components/diff-set/diff-provider';
import { useIntl } from 'src/hooks/intl';
import { extractFilepath } from 'src/utils/extract-file-path';

import { useDiffPreferences as useDiffPreferencesDI } from '../../../hooks/diffs';
import { ToggleCommentsItem } from '../../diff-actions-menu/action-items/toggle-comments-item';
import { ToggleSideBySideItem } from '../../diff-actions-menu/action-items/toggle-side-by-side-item';
import { ViewSourceItem } from '../../diff-actions-menu/action-items/view-source-item';
import messages from '../../diff.i18n';
import { usePRViewEntireFileState as useViewEntireFileStateDI } from '../hooks';

type Props = {
  path: string | null;
  isDeleted: boolean;
  useDiffPreferences: typeof useDiffPreferencesDI;
  useViewEntireFileState: typeof useViewEntireFileStateDI;
};

/* eslint @typescript-eslint/ban-types: "warn" */
export const ActionsMenu: React.FC<Props> = ({
  path,
  isDeleted,
  useDiffPreferences = useDiffPreferencesDI,
  useViewEntireFileState = useViewEntireFileStateDI,
}) => {
  const intl = useIntl();
  const {
    diffFile,
    sourceHash: viewEntireFileStateSourceHash,
    destinationHash,
    sourceRepoFullSlug: viewEntireFileStateSourceRepoFullSlug,
    destinationRepoFullSlug,
    allowComments,
  } = useViewEntireFileState();

  const {
    diffMetadataProps: {
      sourceHash: diffSourceHash,
      sourceRepoFullSlug: diffSourceRepoFullSlug,
    },
  } = useContext(DiffContext);

  const sourceHash = viewEntireFileStateSourceHash || diffSourceHash;
  const sourceRepoFullSlug =
    viewEntireFileStateSourceRepoFullSlug || diffSourceRepoFullSlug;

  const { isSideBySide, toggleIsSideBySide } = useDiffPreferences(diffFile);

  const filePath = (diffFile && extractFilepath(diffFile)) || '';

  return (
    <DropdownMenu
      placement="bottom-end"
      testId="view-entire-file-dropdown-menu"
      trigger={({ triggerRef, ...props }) => (
        <Button
          {...props}
          id="actionsMenu"
          appearance="subtle"
          iconBefore={
            <MoreIcon
              label={intl.formatMessage(messages.diffActionsButtonLabel)}
            />
          }
          ref={triggerRef}
        />
      )}
    >
      <DropdownItemGroup>
        <ToggleSideBySideItem
          onSideBySide={toggleIsSideBySide}
          isSideBySide={isSideBySide}
        />
        {allowComments && <ToggleCommentsItem filepath={filePath} />}
        {path && sourceHash && sourceRepoFullSlug && (
          <ViewSourceItem
            filepath={path}
            isDeleted={isDeleted}
            sourceHash={sourceHash}
            destinationHash={destinationHash}
            sourceRepoFullSlug={sourceRepoFullSlug}
            destinationRepoFullSlug={destinationRepoFullSlug}
          />
        )}
      </DropdownItemGroup>
    </DropdownMenu>
  );
};
